import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import type { Bid, BidMetaFields } from '../lib/api'
import { normalizeThorId } from '../lib/thor'
import { ThorIdField, type ThorHit } from './ThorIdField'

/** What the form submits: the name, plus whichever identity fields were filled in. */
export type NewBidFields = { name: string } & BidMetaFields

/**
 * "Create a bid" — the landing's multi-field form: Thor ID, Client, Bid name, in that order, because
 * a Thor hit prefills the two fields after it. Only the name is required; the other two are
 * optional, and neither a missing Thor ID nor a lookup that found nothing (or could not reach Thor)
 * ever blocks creating the bid. The one other condition is that a Thor ID, once typed, must be an
 * OP#.
 *
 * The bid name IS the opportunity's name — there is no separate Opportunity field. A hit fills
 * Client ← Thor's account name and Bid name ← Thor's opportunity name, replacing whatever is in
 * them (the member asked for the lookup), but leaves both EDITABLE (unlike Dealtracker, which locks
 * them): they become the bid's visible label, and members shorten Thor's names.
 *
 * Same chrome and a11y as PromptModal (portal, modal dialog, Escape, focus in on open and back on
 * close), but a real <form>: submitting is the form's job, not a per-input Enter handler.
 */
export function CreateBidModal({
  bids = [],
  onSubmit,
  onClose,
}: {
  /** The caller's bids, for the non-blocking "this Thor ID is already on one of your bids" note. */
  bids?: Bid[]
  onSubmit: (fields: NewBidFields) => Promise<void> | void
  onClose: () => void
}) {
  const { t } = useLang()
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [thorId, setThorId] = useState('')
  const [client, setClient] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Focus the first field on open; hand focus back to whatever opened the dialog on close.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus()
    return () => prev?.focus()
  }, [])

  const canonicalThorId = normalizeThorId(thorId)
  const thorIdOk = thorId.trim() === '' || canonicalThorId !== null
  const canSubmit = name.trim() !== '' && thorIdOk && !submitting
  const duplicate =
    canonicalThorId !== null && bids.some((b) => b.thorId === canonicalThorId) ? t('thor_duplicate') : null

  function applyThorHit(hit: ThorHit): void {
    // A hit replaces both fields; a name Thor does not have (null) leaves its field as typed.
    if (hit.client) setClient(hit.client)
    if (hit.opportunity) setName(hit.opportunity)
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!canSubmit) return
    const fields: NewBidFields = { name: name.trim() }
    if (client.trim()) fields.client = client.trim()
    // Sent canonical; the server normalises again and is the authority.
    if (canonicalThorId) fields.thorId = canonicalThorId
    setSubmitting(true)
    try {
      await onSubmit(fields)
    } catch {
      // Already reported by the API client's global error dialog — just let the member retry.
    } finally {
      setSubmitting(false)
    }
  }

  const optional = <span className="field-optional">{t('field_optional')}</span>

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="modal prompt-modal createbid-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2 id={titleId}>{t('new_bid_title')}</h2>
          <button type="button" className="modal-close" aria-label={t('close')} onClick={onClose}>✕</button>
        </div>
        <form className="modal-body prompt-body createbid-form" onSubmit={(e) => void submit(e)}>
          <ThorIdField
            value={thorId}
            onChange={setThorId}
            onFound={applyThorHit}
            label={<>{t('bid_thor_id')} {optional}</>}
            warning={duplicate}
          />
          <label className="prompt-label">
            {t('bid_client')} {optional}
            <input
              className="prompt-input"
              value={client}
              autoComplete="off"
              onChange={(e) => setClient(e.target.value)}
            />
          </label>
          <label className="prompt-label">
            {t('bid_name')}
            <input
              className="prompt-input"
              value={name}
              required
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="prompt-acts">
            <button type="button" className="mini ghost" onClick={onClose}>{t('cancel')}</button>
            <button type="submit" className="mini ok" disabled={!canSubmit}>
              {t('create')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
